/**
 * Centralized Unlockable Inventory Items System (ES Module)
 *
 * This file defines all unlockable inventory items across the site.
 * Each item specifies:
 * - elementId: The DOM element ID
 * - page: The page where the item appears
 * - unlockMethod: Function to call when item is clicked and inventory is unlocked
 * - visualIndicator: Whether to show the rainbow border when unlocked
 *
 * Usage:
 * 1. Add new items to the UNLOCKABLE_ITEMS array
 * 2. Include this script on pages with unlockable items
 * 3. The system will automatically handle visual states and click events
 */

const UNLOCKABLE_ITEMS = [
     {
          elementId: 'petrole-image',
          page: 'promesses',
          unlockMethod: 'unlockPetroleItem',
          visualIndicator: true,
          description: 'Petrole image in features section'
     }
     // Add more unlockable items here as they are implemented
];

/**
 * Initialize unlockable inventory items system
 * This should be called after DOM is ready and floatingInventory is available
 */
function initializeUnlockableItems() {
     console.log('🎒 Initializing unlockable inventory items system...');

     // Check if floatingInventory is available
     if (typeof floatingInventory === 'undefined') {
          console.warn('⚠️ FloatingInventory not available, unlockable items system delayed...');
          // Retry in 500ms
          setTimeout(initializeUnlockableItems, 500);
          return;
     }

     UNLOCKABLE_ITEMS.forEach(item => {
          const element = document.getElementById(item.elementId);
          if (!element) {
               console.warn(`⚠️ Unlockable item element not found: ${item.elementId}`);
               return;
          }

          // Add CSS class for styling
          element.classList.add('unlockable-inventory-item');

          // Function to update visual state
          const updateVisualState = () => {
               if (floatingInventory.isInventoryUnlocked()) {
                    element.classList.add('inventory-unlocked');
                    if (item.visualIndicator) {
                         element.classList.add('show-unlock-indicator');
                    }
               } else {
                    element.classList.remove('inventory-unlocked', 'show-unlock-indicator');
               }
          };

          // Initial visual state check
          updateVisualState();

          // Set up periodic check (in case inventory unlocks later)
          const checkInterval = setInterval(updateVisualState, 1000);

          // Add click handler
          element.addEventListener('click', () => {
               // Skip unlock method call on promesses page to avoid duplicate popups
               if (item.page === 'promesses') {
                    console.log(`🎯 Skipping unlock method for ${item.elementId} on promesses page`);
                    return;
               }

               if (floatingInventory.isInventoryUnlocked()) {
                    // Call the unlock method
                    if (typeof floatingInventory[item.unlockMethod] === 'function') {
                         floatingInventory[item.unlockMethod]();
                    } else {
                         console.warn(`⚠️ Unlock method not found: ${item.unlockMethod}`);
                    }
               }
               // If inventory not unlocked, do nothing (part of the game discovery)
          });

          console.log(`✅ Initialized unlockable item: ${item.elementId} (${item.description})`);
     });

     console.log(`🎒 Unlockable inventory items system initialized with ${UNLOCKABLE_ITEMS.length} items`);
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
     // Small delay to ensure floatingInventory is loaded
     setTimeout(initializeUnlockableItems, 100);
});

// Export for potential use in other scripts
export const UnlockableInventoryItems = {
     items: UNLOCKABLE_ITEMS,
     initialize: initializeUnlockableItems,
     addItem: (item) => UNLOCKABLE_ITEMS.push(item)
};

export { initializeUnlockableItems };
